/* eslint-disable no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Col,
  DatePicker,
  Input,
  Layout,
  Modal,
  Row,
  Select,
  Typography,
} from 'antd';
import { omit, startCase } from 'lodash';
import queryString from 'query-string';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

import { ProfileContext } from 'contexts/profile';
import InspectionService from 'services/inspection';
import { errHandling } from 'utils';

import PdButton from 'components/pd-button';
import { TableList } from 'components/table-list';
import { AddRentToInspection } from './addRentForm';

import './style.scss';

const { Content } = Layout;
const { Option } = Select;
const { Search } = Input;
const { Text } = Typography;
const { RangePicker } = DatePicker;

export function Inspection() {
  const location = useLocation();
  const { fetchProfile } = useContext(ProfileContext);

  const [currentPage, setCurrentPage] = useState(1);
  const [endDatePick, setEndDatePick] = useState('');
  const [inspectionList, setInspectionList] = useState([]);
  const [loadStatus, setLoadStatus] = useState(true);
  const [nextPage, setNextPage] = useState(-1);
  const [searchValue, setSearchValue] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [startDatePick, setStartDatePick] = useState('');
  const [status, setStatus] = useState('');

  const dateFormat = 'YYYY/MM/DD';
  const navigate = useNavigate();
  const parsed = queryString.parse(location.search);

  const inspectionStatusList = [
    '',
    'awaiting_inspection',
    'check_inspection',
    'done_inspection',
    'ongoing_inspection',
  ];

  const buttonAction = (row = {}) => {
    const { id, status } = row;
    return (
      <Row className="button-table">
        <Col lg={24}>
          <PdButton
            className="height-32"
            full
            onClick={handleToInspectionDetail.bind(this, id, status)}
            type={status === 'awaiting_inspection' ? 'danger' : 'primary'}>
            {status === 'awaiting_inspection' ? 'Inspect' : 'Detail'}
          </PdButton>
        </Col>
      </Row>
    );
  };

  const columns = [
    {
      name: 'Order Id',
      selector: row => row.formatted_id,
      center: true,
    },
    {
      name: 'Status',
      selector: row => row.status,
      format: (row) => startCase(row.status),
      center: true,
    },
    {
      name: 'Request Date',
      selector: row => row.request_inspect_at,
      format: (row) => {
        const momentDate = dayjs(row.request_inspect_at);
        return momentDate.isValid() ? momentDate.format(dateFormat) : '-';
      },
      center: true,
    },
    {
      name: '',
      cell: buttonAction,
      width: '16%',
    },
  ];

  function handleToInspectionDetail(inspectionId, inspectionStatus) {
    const search = queryString.stringify({
      id: inspectionId,
      status: inspectionStatus,
    });
    navigate({
      pathname: '/inspections/detail',
      search,
    });
  }

  function onChangeDate(_, datesString) {
    const [startDate, endDate] = datesString;
    if (!startDate) delete parsed['startDate'];
    else parsed['startDate'] = `${startDate} 00:00`;

    if (!endDate) delete parsed['endDate'];
    else parsed['endDate'] = `${endDate} 23:59`;

    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/inspections',
      search: stringified,
    });
  }

  async function fetchInspection({
    inspect_end_date,
    inspect_start_date,
    limit = 10,
    page,
    q,
    status,
  } = {}) {
    setLoadStatus(true);
    try {
      const { data } = await InspectionService.getRentOrderList({
        cursor: page,
        inspect_end_date,
        inspect_start_date,
        limit,
        q,
        status,
      });
      const { next_cursor } = data.metadata;
      setInspectionList(data.data);
      setNextPage(next_cursor);
    } catch (error) {
      errHandling(error);
    } finally {
      setShowModal(false);
      setLoadStatus(false);
    }
  }

  function changePage(page) {
    if (page < 1) page = 1;
    parsed.page = page;
    const search = queryString.stringify(parsed);
    navigate({
      pathname: '/inspections',
      search,
    });
  }

  function filterInspection(param, val) {
    if (val === '' || val === 0) {
      delete parsed[param];
    } else {
      parsed[param] = val;
    }
    parsed.page = 1;
    const search = queryString.stringify(parsed);
    navigate({
      pathname: '/inspections',
      search,
    });
  }

  function handleCloseModal() {
    setShowModal(false);
  }

  function resetFilter() {
    const search = queryString.stringify(
      omit(parsed, ['page', 'q', 'status', 'startDate', 'endDate'])
    );
    navigate({
      pathname: '/inspections',
      search,
    });
  }

  function handleOnChangeSearch({ target: { value } = {} } = {}) {
    setSearchValue(value);
  }

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* eslint-disable */
  useEffect(() => {
    if (parsed.page) {
      if (parsed.page < 1) {
        parsed.page = 1;
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: '/inspections',
          search: stringified,
        });
      } else setCurrentPage(parsed.page);
    } else setCurrentPage(1);

    if (parsed.q) setSearchValue(parsed.q);
    else setSearchValue('');

    if (parsed.status) setStatus(parsed.status);
    else setStatus('');

    if (parsed.endDate) setEndDatePick(parsed.endDate);
    else setEndDatePick('');

    if (parsed.startDate) setStartDatePick(parsed.startDate);
    else setStartDatePick('');

    fetchInspection({
      inspect_end_date: parsed.endDate,
      inspect_start_date: parsed.startDate,
      page: parsed.page,
      q: parsed.q,
      status: parsed.status,
    });
  }, [parsed.page, parsed.q, parsed.status, parsed.startDate, parsed.endDate]);
  /* eslint-enable */

  return (
    <Layout className="main-container pd-cms-inspection">
      <div className="content">
        <div className="pageTitle">Inspection</div>
        <Content className="pageContent">
          <Row
            align="middle"
            className="pd-margin-bottom-sm"
            gutter={[18, 36]}
            type="flex">
            <Col lg={12}>
              <Search
                className="search-product"
                enterButton
                onChange={handleOnChangeSearch}
                onSearch={filterInspection.bind(this, 'q')}
                placeholder="Search Inspection"
                value={searchValue}
              />
            </Col>
            <Col lg={3}>
              <Select
                className="w-full"
                onChange={filterInspection.bind(this, 'status')}
                value={status}>
                {inspectionStatusList.map((statusEl, idx) => (
                  <Option
                    hidden={!statusEl}
                    key={idx}
                    selected={!statusEl}
                    value={statusEl}>
                    {startCase(statusEl.split('_')[0]) || 'Status'}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col lg={5}>
              <Text className="reset-filter" onClick={resetFilter}>
                Reset Filter
              </Text>
            </Col>
            <Col lg={4}>
              <PdButton
                className="height-32 pd-padding-right-md pd-padding-left-md"
                full
                onClick={() => setShowModal(true)}
                type="primary">
                Add Rent
              </PdButton>
            </Col>
          </Row>

          <Row className="pd-margin-bottom-md">
            <Col lg={12}>
              <RangePicker
                className="w-full"
                format={dateFormat}
                onChange={onChangeDate}
                value={[
                  startDatePick && dayjs(startDatePick, dateFormat),
                  endDatePick && dayjs(endDatePick, dateFormat),
                ]}
              />
            </Col>
          </Row>

          <Row>
            {!loadStatus && (
              <TableList
                changePage={changePage}
                columns={columns}
                loadingOrder={loadStatus}
                nextPage={nextPage}
                orderList={inspectionList}
                page={currentPage}
                responsive={false}
              />
            )}
          </Row>
        </Content>
      </div>
      {showModal && (
        <Modal
          centered
          footer={false}
          onCancel={handleCloseModal}
          title="Add Rent"
          open={showModal}
          wrapClassName="hidden-wrap-modal">
          <AddRentToInspection fetchInspection={fetchInspection} />
        </Modal>
      )}
    </Layout>
  );
}

export default { Inspection };
